import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { cn } from '@/lib/utils';
import type { Comment } from '@/lib/stores/comments/types';

interface CommentItemProps {
  comment: Comment;
  onLike: (commentId: string) => void;
  onDelete?: (commentId: string) => void;
  onEdit?: (commentId: string, content: string) => void;
  onReport?: (commentId: string, reason: string) => void;
  onModerate?: (commentId: string, action: 'approve' | 'reject') => void;
  onTranslate?: (commentId: string, targetLang: string) => Promise<string>;
  currentUserId?: string;
  currentUserRole?: string;
  onReply?: (commentId: string) => void;
  showReplyButton?: boolean;
}

const formatTimestamp = (value: string | Date) => {
  const date = value instanceof Date ? value : new Date(value);
  return date.toLocaleString();
};

export function CommentItem({
  comment,
  onLike,
  onDelete,
  onReply,
  showReplyButton = true,
}: CommentItemProps) {
  const userName = comment.user?.name || `User ${comment.userId}`;
  const initials = userName.charAt(0).toUpperCase();

  return (
    <div
      className={cn(
        'rounded-lg border bg-card p-3 shadow-sm transition-opacity',
        comment.isPending && 'opacity-70'
      )}
    >
      <div className="flex items-start gap-3">
        <Avatar className="h-8 w-8">
          <AvatarImage src={comment.user?.profilePictureUrl || undefined} />
          <AvatarFallback>{initials}</AvatarFallback>
        </Avatar>

        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-2">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium truncate">{userName}</span>
                {comment.isPending && (
                  <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-700">
                    Sending...
                  </span>
                )}
              </div>
              <p className="text-xs text-muted-foreground">{formatTimestamp(comment.createdAt)}</p>
            </div>

            <div className="flex items-center gap-1">
              <Button variant="ghost" size="sm" disabled={comment.isPending} onClick={() => onLike(comment.id.toString())}>
                Like {comment.likes}
              </Button>
              {showReplyButton && onReply && (
                <Button variant="ghost" size="sm" disabled={comment.isPending} onClick={() => onReply(comment.id.toString())}>
                  Reply
                </Button>
              )}
              {onDelete && (
                <Button variant="ghost" size="sm" disabled={comment.isPending} onClick={() => onDelete(comment.id.toString())}>
                  Delete
                </Button>
              )}
            </div>
          </div>

          <p className="mt-2 whitespace-pre-wrap text-sm leading-6">{comment.content}</p>
        </div>
      </div>
    </div>
  );
}

export function ReplyItem(props: {
  reply: Comment;
  onLike: (commentId: string) => void;
  onReply: (commentId: string) => void;
  isModerator?: boolean;
  onDelete?: (commentId: string) => void;
}) {
  return <CommentItem comment={props.reply} onLike={props.onLike} onReply={props.onReply} onDelete={props.onDelete} />;
}
